// src/pages/customerComponents/RemoteBusinessComponents/ProcessSteps.js

import React, { useEffect, useRef, useState } from 'react';
import '../../../styles/CustomerSiteStyles/ProcessSteps.css';

const steps = [
  {
    number: '01',
    title: 'Speak With Our Team',
    bullets: [
      'Connect with us virtually via Zoom — no in-person visit required.',
      'We\'ll walk you through our entire process from start to finish.',
      'Ask any questions you have before we begin.',
      'Learn about our Homeowner Partnership Program — earn $300 per qualifying referral with no fees, no commitment, and no cap.',
    ],
    icon: '💬',
  },
  {
    number: '02',
    title: 'Notify Your Insurance Company',
    bullets: [
      'We guide you step by step through filing your storm damage claim.',
      'Our team knows exactly what information your insurer will ask for.',
      'No guesswork — we make this call simple and stress-free.',
    ],
    icon: '📞',
  },
  {
    number: '03',
    title: 'Industry Expert Inspections',
    bullets: [
      'Our certified inspector documents all storm-related damage to your property.',
      'Thanks to our state-of-the-art virtual inspection system, there is no need to be home in most cases.',
      'Your insurance company will also conduct their own inspection to verify the damage and write their estimate.',
      'We stay involved throughout both inspections to ensure nothing gets missed and every detail is accurately captured.',
    ],
    icon: '🔍',
  },
  {
    number: '04',
    title: 'Approval & Pre-Build Setup',
    bullets: [
      'Our team works with your insurance company to finalize the complete scope of work.',
      'We advocate on your behalf to make sure every necessary repair is covered.',
      'Once approved, we coordinate directly with you to schedule a build date that fits your life.',
      'All logistics are handled on our end so the process stays smooth and stress-free.',
    ],
    icon: '📋',
  },
  {
    number: '05',
    title: 'The Build',
    bullets: [
      'Our certified and insured crew completes all repairs and installations per the approved scope.',
      'Work is done on time, to code, and with quality craftsmanship.',
      'We treat your property with the same care we\'d give our own.',
      'After the build, a company representative will walk the property with you to ensure everything is done right and to your complete satisfaction.',
    ],
    icon: '🔨',
  },
  {
    number: '06',
    title: 'Final Touches',
    bullets: [
      'Once your insurance releases all funds, we send your final invoice.',
      'Pay quickly and securely online — no complicated process.',
      'We\'ll provide your warranty paperwork so everything is documented and at your fingertips.',
      'Simple, transparent, and hassle-free right to the very end.',
    ],
    icon: '💳',
  },
];

const StepCard = ({ step, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let observer;
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (observer) observer.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      if (ref.current) observer.observe(ref.current);
      return () => { if (observer) observer.disconnect(); };
    }
    setVisible(true);
    return undefined;
  }, []);

  return (
    <div
      ref={ref}
      className={`process-step ${visible ? 'process-step--visible' : ''}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Number column — sits outside card flow via CSS */}
      <div className="process-step__left">
        <div className="process-step__number">{step.number}</div>
        <div className="process-step__connector" />
      </div>

      {/* Card — this is what gets centered */}
      <div className="process-step__card">
        <div className="process-step__icon">{step.icon}</div>
        <div className="process-step__content">
          <h3 className="process-step__title">{step.title}</h3>
          <ul className="process-step__bullets">
            {step.bullets.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const ProcessSteps = () => {
  return (
    <section className="process-section">
      <div className="process-section__header">
        <div className="process-section__tag">Our Process</div>
        <h2 className="process-section__title">How It Works, Step by Step</h2>
        <p className="process-section__subtitle">
          We handle the complexity so you don&apos;t have to. Here&apos;s exactly
          what to expect from start to finish.
        </p>
      </div>

      {/* Outer wrapper centers the cards. Inner offset makes room for numbers. */}
      <div className="process-steps-outer">
        <div className="process-steps-list">
          {steps.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} />
          ))}
        </div>
      </div>

    </section>
  );
};

export default ProcessSteps;